import { cn } from '@/utils'
import type { FC, HTMLAttributes } from 'react'
import { useMemo } from 'react'
import { millisecondsToHms, roundMsToSeconds } from '@rundown-studio/timeutils'
import { PlusSignIndicator } from './plus-sign-indicator'
import { TimeDisplay } from './time-display'

interface DimmedDurationProps extends HTMLAttributes<HTMLDivElement> {
  ms: number
  /**
   * Rounding function to use when rounding to seconds.
   * - Math.ceil (default): For countdowns/remaining time (1-1000ms shows as "1s")
   * - Math.floor: For completed/elapsed durations (1001-1999ms shows as "1s")
   */
  roundingFn?: (n: number) => number
}

/**
 * Renders a duration in the format 00h 00m 00s,
 * dimming leading zero units for clarity and UI stability.
 * If ms is negative, a plus sign is shown absolutely to the left.
 */
export const DimmedDuration: FC<DimmedDurationProps> = ({
  ms,
  roundingFn = Math.ceil,
  className,
  ...divProps
}) => {
  const { isNegative, timeParts } = useMemo(() => {
    const roundedMs = !Number.isFinite(ms)
      ? 0
      : roundMsToSeconds(ms, roundingFn)

    return {
      isNegative: roundedMs < 0,
      timeParts: millisecondsToHms(Math.abs(roundedMs)),
    }
  }, [ms, roundingFn])

  return (
    <div
      className={cn(
        'flex justify-end gap-1 items-center isolate relative',
        className,
      )}
      {...divProps}
    >
      {isNegative && <PlusSignIndicator />}
      <TimeDisplay {...timeParts} />
    </div>
  )
}
